import React, { useState, useEffect } from 'react';
import { 
  LayoutDashboard, Building2, Users, Target, Settings, TrendingUp,
  LogOut, ShieldCheck, ChevronRight, Bell,
  User, Lock, X, KeyRound, Eye, EyeOff, Menu, Briefcase, MessageSquare
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { bdAPI } from '../utils/api';

const BDLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPass, setShowPass] = useState({ old: false, new: false, confirm: false });
  const [loading, setLoading] = useState(false);
  const [bdInfo, setBDInfo] = useState({
    name: 'BD Manager',
    user_id: '...',
    email: '',
    agencies_count: 0
  });

  useEffect(() => {
    const fetchBDInfo = async () => {
      try {
        const storedUser = sessionStorage.getItem('bdUser');
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser);
            setBDInfo(prev => ({
              ...prev,
              name: `${parsed.first_name || ''} ${parsed.last_name || ''}`.trim() || 'BD Manager',
              user_id: parsed.user_id || parsed.xoxo_id || '',
              email: parsed.email || ''
            }));
          } catch (e) {}
        }

        const res = await bdAPI.getProfile();
        if (res.data?.bd) {
          const bd = res.data.bd;
          setBDInfo({
            name: `${bd.first_name || ''} ${bd.last_name || ''}`.trim() || 'BD Manager',
            user_id: bd.user_id || bd.xoxo_id || '',
            email: bd.email || '',
            agencies_count: bd.agencies_count || 0
          });
        }
      } catch (err) {
        console.error("Failed to fetch BD info:", err);
      }
    };
    fetchBDInfo();
  }, []);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: '/bd/dashboard' },
    { id: 'agencies', label: 'My Agencies', icon: <Building2 size={20} />, path: '/bd/agencies' },
    { id: 'hosts', label: 'Agency Hosts', icon: <Users size={20} />, path: '/bd/hosts' },
    { id: 'performance', label: 'Performance & Activity', icon: <TrendingUp size={20} />, path: '/bd/performance' },
    { id: 'support', label: 'Help & Complaints', icon: <MessageSquare size={20} />, path: '/bd/support' },
    { id: 'settings', label: 'Account Settings', icon: <Settings size={20} />, path: '/bd/settings' },
  ];

  const handleLogout = () => {
    sessionStorage.removeItem('bdToken');
    sessionStorage.removeItem('bdUser');
    window.location.href = '/bd/login';
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert("New passwords do not match!");
      return;
    }
    setLoading(true);
    try {
      await bdAPI.changePassword({ 
        oldPassword: passwordData.oldPassword, 
        newPassword: passwordData.newPassword 
      });
      setShowPasswordModal(false);
      setPasswordData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password updated successfully!');
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#020617] text-slate-200 font-['Outfit'] overflow-x-hidden">
      {/* Sidebar */}
      <aside className={`
        fixed md:sticky top-0 inset-y-0 left-0 z-50 h-screen w-64 md:w-72 bg-[#0f172a] border-r border-white/5 backdrop-blur-xl flex flex-col transition-transform duration-300 overflow-hidden
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Top Header & Profile Card (Fixed at top) */}
        <div className="shrink-0">
          {/* Logo Header */}
          <div className="p-4 md:p-5 flex items-center justify-between border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-gradient-to-tr from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <Briefcase size={20} className="text-white" />
              </div>
              <div>
                <h1 className="text-sm font-black text-white tracking-tight uppercase">XOXO <span className="text-emerald-400">BD</span></h1>
                <p className="text-[8px] text-slate-400 font-bold tracking-widest uppercase">Business Development</p>
              </div>
            </div>
            <button 
              onClick={() => setIsSidebarOpen(false)} 
              className="md:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 cursor-pointer"
            >
              <X size={18} />
            </button>
          </div>

          {/* BD Profile Card Pill */}
          <div className="mx-3 mt-3 p-3 rounded-2xl bg-white/[0.03] border border-white/5">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 flex items-center justify-center text-emerald-400 font-bold text-xs shrink-0">
                BD
              </div>
              <div className="overflow-hidden min-w-0">
                <h4 className="text-xs font-bold text-white truncate">{bdInfo.name}</h4>
                <div className="inline-block px-1.5 py-0.2 rounded-full bg-emerald-500/10 text-emerald-400 text-[9px] font-mono font-bold">
                  {bdInfo.user_id}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scrollable Navigation Menu */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-1 min-h-0 custom-scrollbar">
          {menuItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl font-semibold text-xs transition-all duration-200 cursor-pointer ${
                  active 
                    ? 'bg-gradient-to-r from-emerald-500 to-cyan-500 text-white shadow-md shadow-emerald-500/20' 
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2.5">
                  {item.icon}
                  <span>{item.label}</span>
                </div>
                {active && <ChevronRight size={14} />}
              </button>
            );
          })}
        </nav>

        {/* Footer / Logout (ALWAYS Pinned) */}
        <div className="p-3 border-t border-white/5 shrink-0 bg-[#0f172a]">
          <button 
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl font-bold text-xs text-rose-400 bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 hover:border-rose-500/40 transition-colors cursor-pointer"
          >
            <LogOut size={16} />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Navbar */}
        <header className="sticky top-0 z-40 h-20 bg-[#020617]/80 backdrop-blur-xl border-b border-white/5 px-6 md:px-10 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => setIsSidebarOpen(true)} 
              className="md:hidden p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5"
            >
              <Menu size={24} />
            </button>
            <div className="hidden sm:block">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Portfolio Portal</span>
              <h2 className="text-lg font-black text-white capitalize">
                {menuItems.find(m => m.path === location.pathname)?.label || 'BD Portal'}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Password Modal Button */}
            <button 
              onClick={() => setShowPasswordModal(true)}
              className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-bold text-slate-300 hover:text-white transition-all cursor-pointer"
            >
              <KeyRound size={15} className="text-emerald-400" />
              <span className="hidden md:inline">Change Password</span>
            </button>

            {/* Direct Sign Out in Top Navbar */}
            <button 
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-xs font-bold text-rose-300 hover:text-white transition-all cursor-pointer"
              title="Sign Out"
            >
              <LogOut size={14} className="text-rose-400" />
              <span className="hidden md:inline">Sign Out</span>
            </button>

            {/* BD Profile Header Badge */}
            <div className="flex items-center gap-3 pl-2 border-l border-white/10">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-emerald-500 to-cyan-500 flex items-center justify-center text-white font-bold text-sm shadow-md shadow-emerald-500/20">
                {bdInfo.name.charAt(0).toUpperCase()}
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-xs font-bold text-white">{bdInfo.name}</p>
                <p className="text-[10px] text-emerald-400 font-mono font-semibold">{bdInfo.user_id}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content Body */}
        <main className="flex-1 p-6 md:p-10 max-w-7xl w-full mx-auto">
          {children}
        </main>
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-in fade-in">
          <div className="bg-[#0f172a] border border-white/10 rounded-[32px] p-8 max-w-md w-full shadow-2xl relative">
            <button 
              onClick={() => setShowPasswordModal(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/5"
            >
              <X size={20} />
            </button>

            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                <Lock size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white">Change Password</h3>
                <p className="text-xs text-slate-400">Update your BD portal login password</p>
              </div>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">Current Password</label>
                <div className="relative">
                  <input
                    type={showPass.old ? 'text' : 'password'}
                    required
                    value={passwordData.oldPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500 text-sm"
                    placeholder="Enter current password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, old: !showPass.old })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPass.old ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPass.new ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500 text-sm"
                    placeholder="At least 6 characters"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, new: !showPass.new })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPass.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-400 block mb-2">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showPass.confirm ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-slate-600 outline-none focus:border-emerald-500 text-sm"
                    placeholder="Re-type new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass({ ...showPass, confirm: !showPass.confirm })}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                  >
                    {showPass.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-white/10 text-slate-400 hover:text-white text-sm font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white text-sm font-bold shadow-lg shadow-emerald-500/20 disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BDLayout;
